import { createContext, useContext } from 'react';
import type { MutableRefObject } from 'react';
import type { IdentityCheckQuestion, WebApiTerm } from '@zg-rentals/ts-id-verification-client';
import type { ExitReason, IdvStep, LogData, LogLevel } from '../../../models';
import { TermSet } from '../../../models';
import type { EmitMetric, TrackEvent } from '../clickstream/events';

function noop(): void {}

export interface PidAnswers {
  firstName: string;
  middleName: string;
  lastName: string;
  currentAddress: string;
  currentCity: string;
  currentState: string;
  currentZip: string;
  phone: string;
  dob: string;
  ssn: string;
}

interface IdVerificationContextOptions {
  id: string;
  termSet: TermSet;
  terms: Array<WebApiTerm>;
  setTerms: (terms: Array<WebApiTerm>) => void;
  zgApi: string;
  skipLandingStep: boolean;
  reauthenticating: boolean;
  setReauthenticating: (reauthenticating: boolean) => void;
  skipTermsStep: boolean | null;
  setSkipTermsStep: (skipTermsStep: boolean | null) => void;
  handleScreeningReports: boolean;
  onTermChanged: () => void;
  onSubmitKbaForm: () => void;
  latestOnNotAuthenticated: MutableRefObject<() => void>;
  latestOnTermAccepted: MutableRefObject<(id: string) => void>;
  latestOnTermDeclined: MutableRefObject<(id: string) => void>;
  latestOnKbaStepUp: MutableRefObject<() => void>;
  latestOnIdVerified: MutableRefObject<() => void>;
  latestOnSubmitPidForm: MutableRefObject<(answers: PidAnswers) => void>;
  latestOnExit: (exitReason: { reason: ExitReason; success: boolean }) => void | Promise<void>;
  latestOnStepChange: MutableRefObject<({ step }: { step: IdvStep }) => void>;
  latestOnError: MutableRefObject<({ code }: { code: string }) => void>;
  latestTrackEvent: MutableRefObject<TrackEvent>;
  latestEmitMetric: MutableRefObject<EmitMetric>;
  latestLog: MutableRefObject<(logLevel: LogLevel, logData: LogData) => void>;
  kiqQuestionSet: Array<IdentityCheckQuestion>;
  setKiqQuestionSet: (questionSet: Array<IdentityCheckQuestion>) => void;
  pidAnswers: PidAnswers;
  setPidAnswers: (callback: (pidAnswers: PidAnswers) => PidAnswers) => void;
  loggedIn: boolean;
  setLoggedIn: (isLoggedIn: boolean) => void;
  pidRemainingAttempts: number | null;
  setPidRemainingAttempts: (remainingAttempts: number | null) => void;
  passiveAuthData: Record<string, string>;
  setPassiveAuthData: (passiveAuthData: Record<string, string>) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  isOnExitLoading?: boolean;
}

export const DEFAULT_OPTIONS: IdVerificationContextOptions = {
  id: 'id-verification',
  zgApi: typeof window !== 'undefined' ? window.location.origin : '',
  termSet: TermSet.RenterScreening,
  terms: [],
  setTerms: noop,
  skipLandingStep: false,
  reauthenticating: false,
  setReauthenticating: noop,
  skipTermsStep: null,
  setSkipTermsStep: noop,
  handleScreeningReports: false,
  onTermChanged: noop,
  onSubmitKbaForm: noop,
  latestOnNotAuthenticated: { current: noop },
  latestOnTermAccepted: { current: noop },
  latestOnTermDeclined: { current: noop },
  latestOnKbaStepUp: { current: noop },
  latestOnIdVerified: { current: noop },
  latestOnSubmitPidForm: { current: noop },
  latestOnExit: noop,
  latestOnStepChange: { current: noop },
  latestOnError: { current: noop },
  latestTrackEvent: { current: noop },
  latestEmitMetric: { current: noop },
  latestLog: { current: noop },
  kiqQuestionSet: [],
  setKiqQuestionSet: noop,
  pidAnswers: {
    firstName: '',
    middleName: '',
    lastName: '',
    currentAddress: '',
    currentCity: '',
    currentState: '',
    currentZip: '',
    phone: '',
    dob: '',
    ssn: '',
  },
  setPidAnswers: noop,
  loggedIn: false,
  setLoggedIn: noop,
  pidRemainingAttempts: null,
  setPidRemainingAttempts: noop,
  passiveAuthData: {
    jsc: '',
    hdimPayload: '',
  },
  setPassiveAuthData: noop,
  loading: false,
  setLoading: noop,
  isOnExitLoading: false,
};

const IdVerificationContext = createContext<IdVerificationContextOptions>(DEFAULT_OPTIONS);

export default IdVerificationContext;

export function useId() {
  const { id } = useContext(IdVerificationContext);
  return id;
}
export function useZgApi() {
  const { zgApi } = useContext(IdVerificationContext);
  return zgApi;
}

export function useTermSet() {
  const { termSet } = useContext(IdVerificationContext);
  return termSet;
}

export function useReauthenticating() {
  const { reauthenticating } = useContext(IdVerificationContext);
  return reauthenticating;
}

export function useSetReauthenticating() {
  const { setReauthenticating } = useContext(IdVerificationContext);
  return setReauthenticating;
}

export function useSkipLandingStep() {
  const { skipLandingStep } = useContext(IdVerificationContext);
  return skipLandingStep;
}

export function useSkipTermsStep() {
  const { skipTermsStep } = useContext(IdVerificationContext);
  return skipTermsStep;
}

export function useSetSkipTermsStep() {
  const { setSkipTermsStep } = useContext(IdVerificationContext);
  return setSkipTermsStep;
}

export function useHandleScreeningReports() {
  const { handleScreeningReports } = useContext(IdVerificationContext);
  return handleScreeningReports;
}

export function useOnSubmitPidForm() {
  const { latestOnSubmitPidForm } = useContext(IdVerificationContext);
  return latestOnSubmitPidForm.current;
}

export function useIdVerificationCallbacks() {
  const {
    latestOnNotAuthenticated,
    latestOnSubmitPidForm,
    latestOnTermAccepted,
    latestOnTermDeclined,
    latestOnExit,
    latestOnStepChange,
    latestOnKbaStepUp,
    latestOnError,
    latestTrackEvent,
    latestEmitMetric,
    latestLog,
    isOnExitLoading,
  } = useContext(IdVerificationContext);
  return {
    onNotAuthenticated: latestOnNotAuthenticated.current,
    onSubmitPidForm: latestOnSubmitPidForm.current,
    onKbaStepUp: latestOnKbaStepUp.current,
    onTermAccepted: latestOnTermAccepted.current,
    onTermDeclined: latestOnTermDeclined.current,
    onExit: latestOnExit,
    onStepChange: latestOnStepChange.current,
    onError: latestOnError.current,
    trackEvent: latestTrackEvent.current,
    emitMetric: latestEmitMetric.current,
    log: latestLog.current,
    isOnExitLoading,
  };
}

export function useKiqQuestionSet() {
  const { kiqQuestionSet } = useContext(IdVerificationContext);
  return kiqQuestionSet;
}

export function useSetKiqQuestionSet() {
  const { setKiqQuestionSet } = useContext(IdVerificationContext);
  return setKiqQuestionSet;
}

export function usePidAnswers() {
  const { pidAnswers } = useContext(IdVerificationContext);
  return pidAnswers;
}

export function useSetPidAnswers() {
  const { setPidAnswers } = useContext(IdVerificationContext);
  return setPidAnswers;
}

export function useLoggedIn() {
  const { loggedIn } = useContext(IdVerificationContext);
  return loggedIn;
}

export function useSetLoggedIn() {
  const { setLoggedIn } = useContext(IdVerificationContext);
  return setLoggedIn;
}

export function usePidRemainingAttempts() {
  const { pidRemainingAttempts } = useContext(IdVerificationContext);
  return pidRemainingAttempts;
}

export function useSetPidRemainingAttempts() {
  const { setPidRemainingAttempts } = useContext(IdVerificationContext);
  return setPidRemainingAttempts;
}

export function usePassiveAuthData() {
  const { passiveAuthData } = useContext(IdVerificationContext);
  return {
    jsc: passiveAuthData?.jsc,
    hdimPayload: passiveAuthData?.hdimPayload,
  };
}

export function useSetPassiveAuthData() {
  const { setPassiveAuthData } = useContext(IdVerificationContext);
  return setPassiveAuthData;
}

export function useLoading() {
  const { loading } = useContext(IdVerificationContext);
  return loading;
}

export function useSetLoading() {
  const { setLoading } = useContext(IdVerificationContext);
  return setLoading;
}

export function useIsOnExitLoading() {
  const { isOnExitLoading } = useContext(IdVerificationContext);
  return isOnExitLoading;
}
